import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, X, Undo2 } from "lucide-react";
import { useAuth } from "../App";
import { loadDraft, clearDraft, fillField, findField } from "../lib/draftKeeper";

// After re-login, puts back text that was being typed when the session expired.
export const DraftRestorer = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [restored, setRestored] = useState({});

  useEffect(() => { if (user) setDraft(loadDraft(user.user_id)); }, [user, location.pathname]);

  const onPath = draft && draft.path === location.pathname + location.search;

  useEffect(() => {
    if (!user || !onPath) return undefined;
    const tryRestore = () => {
      draft.fields.forEach((f) => {
        if (f.copyOnly || restored[f.key]) return;
        let el = null;
        try { el = findField(f.key); } catch { el = null; }
        if (el && !el.value) {
          fillField(el, f.value);
          setRestored((r) => ({ ...r, [f.key]: true }));
          toast.success("Restored your unsaved text");
        }
      });
    };
    tryRestore();
    const obs = new MutationObserver(tryRestore);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [user, onPath, draft, restored]);

  if (!user || !draft) return null;
  const dismiss = () => { clearDraft(); setDraft(null); };
  const copy = (v) => navigator.clipboard?.writeText(v).then(() => toast.success("Copied"));

  return (
    <div className="fixed bottom-4 right-4 z-[60] w-[min(360px,calc(100vw-2rem))] bg-white border border-[#E5DFEC] rounded-2xl shadow-xl p-4" data-testid="draft-restorer">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-medium text-[#6B4E8A]">We saved what you were writing</p>
        <button onClick={dismiss} className="text-[#94A3B8] hover:text-[#6B4E8A]" aria-label="Dismiss" data-testid="draft-restorer-dismiss"><X className="w-4 h-4" /></button>
      </div>
      <p className="text-xs text-[#64748B] mb-3">Your session expired. {onPath ? "We've put your text back where we could — copy anything else below." : "Go back to the page you were on to continue."}</p>
      {!onPath && (
        <button onClick={() => navigate(draft.path)} className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#6B4E8A] rounded-full px-3 py-1.5" data-testid="draft-restorer-goto">
          <Undo2 className="w-3.5 h-3.5" /> Return to where you were
        </button>
      )}
      <ul className="space-y-2 max-h-48 overflow-y-auto">
        {draft.fields.map((f) => (
          <li key={f.key} className="flex items-start gap-2 bg-[#FAF7F2] rounded-lg px-2.5 py-2" data-testid={`draft-field-${f.key}`}>
            <p className="flex-1 min-w-0 text-xs text-[#475569] line-clamp-2 break-words">{f.value}</p>
            <button onClick={() => copy(f.value)} className="text-[#6B4E8A] shrink-0" aria-label="Copy" data-testid={`draft-copy-${f.key}`}><Copy className="w-3.5 h-3.5" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DraftRestorer;
